import { Button, Dropdown, Table, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { TbDotsVertical, TbInfoCircle } from 'react-icons/tb'

import { formatDateTimeUk } from '@/Ship/Helpers/fieldViews'
import { classNames } from '@/Ship/Helpers/classNames'

import { DISK_ROLE_LABEL } from '../Contracts/DiskShare'
import { formatBytes, personName } from '../Helpers/diskFormat'
import { diskBrowserStore as store } from '../Stores/DiskBrowserStore'
import { DiskNodeBadges } from './DiskNodeBadges'
import { DiskNodeIcon } from './DiskNodeIcon'
import { DiskNodePath } from './DiskNodePath'
import { DiskPerson } from './DiskPerson'
import { DISK_PROPS_TRIGGER } from './DiskPropertiesDrawer'
import { useDiskActions } from './useDiskActions'

import type { DiskNode, DiskSortField } from '../Contracts/DiskNode'
import type { TableProps } from 'antd'
import type { DragEvent, HTMLAttributes, MouseEvent } from 'react'

import styles from './Disk.module.css'

export interface DiskNodeTableProps {
    /** Вузька ширина: одна колонка з назвою й підписом під нею. */
    compact: boolean
    /**
     * Планшетна ширина (нижче `xl`): колонки «Автор» і «Хто поділився» йдуть
     * у підпис під назвою — інакше фіксовані колонки з'їдають «Назву» до нуля.
     */
    narrow: boolean
    /** Сортування заголовками (папка й пошук); у «Нещодавніх» порядок бека. */
    sortable: boolean
    /** Показати під назвою, де лежить — повний шлях із переходом у папку (пошук, нещодавні). */
    showLocation: boolean
}

/**
 * Таблиця вмісту: назва з іконкою виду, автор, змінено, розмір, меню дій.
 * У «Спільних зі мною» — назва, хто поділився, коли, моя роль (`DISK-005`
 * §3.6.5). Біля назви — значки доступу (`DiskNodeBadges`).
 *
 * Клік — вибір (Ctrl/Cmd — додати, Shift — діапазон; при відкритому
 * дровері «Властивості» — його вміст), подвійний клік — відкрити (папку —
 * перейти, файл — переглядач). Автор — з аватаром (`DiskPerson`). Рядки тягнуться на папку
 * в таблиці, у крихтах чи в лівій панелі — це переміщення.
 */
export const DiskNodeTable = observer(({ compact, narrow, sortable, showLocation }: DiskNodeTableProps) => {
    const actions = useDiskActions()
    const sharedList = store.isSharedList
    // На вузькій ширині заголовків немає — сортують списком у шапці, а
    // затінена «відсортована» колонка без заголовка читається як вада
    const sortOrder = (field: DiskSortField) =>
        sortable && !compact && store.sort.field === field ? (store.sort.order === 'asc' ? 'ascend' : 'descend') : null

    const sub = (node: DiskNode): string =>
        (sharedList
            ? [
                  compact || narrow ? `від ${personName(node.shared?.sharedBy ?? null)}` : null,
                  compact ? formatDateTimeUk(node.shared?.sharedAt) : null,
                  compact && node.role ? DISK_ROLE_LABEL[node.role] : null,
              ]
            : [
                  compact || narrow ? personName(node.createdBy) : null,
                  compact ? formatDateTimeUk(node.updatedAt) : null,
                  compact && node.type === 'file' ? formatBytes(node.size) : null,
              ]
        )
            .filter((part): part is string => Boolean(part))
            .join(' · ')

    const columns: NonNullable<TableProps<DiskNode>['columns']> = [
        {
            key: 'name',
            title: 'Назва',
            sorter: sortable,
            sortOrder: sortOrder('name'),
            render: (_: unknown, node) => {
                const line = sub(node)

                return (
                    <span className={classNames(styles['nameCell'])}>
                        <DiskNodeIcon kind={node.kind} size={compact ? 32 : 30} />
                        <span className={classNames(styles['nameText'])}>
                            <span className={classNames(styles['nameRow'])}>
                                <span className={classNames(styles['name'])} title={node.name}>
                                    {node.name}
                                </span>
                                <DiskNodeBadges node={node} hideShared={sharedList} />
                            </span>
                            {showLocation && <DiskNodePath node={node} onOpen={actions.openParent} />}
                            {line !== '' && <span className={classNames(styles['sub'])}>{line}</span>}
                        </span>
                    </span>
                )
            },
        },
        {
            key: 'author',
            title: 'Автор',
            width: 190,
            render: (_: unknown, node) => (
                <span className={classNames(styles['author'])} data-testid="disk-author">
                    <DiskPerson user={node.createdBy} />
                </span>
            ),
        },
        {
            key: 'updatedAt',
            title: 'Змінено',
            width: 170,
            sorter: sortable,
            sortOrder: sortOrder('updatedAt'),
            render: (_: unknown, node) => (
                <span className={classNames(styles['muted'])}>{formatDateTimeUk(node.updatedAt)}</span>
            ),
        },
        {
            key: 'size',
            title: 'Розмір',
            width: 110,
            align: 'right',
            sorter: sortable,
            sortOrder: sortOrder('size'),
            render: (_: unknown, node) => (
                <span className={classNames(styles['muted'])}>
                    {node.type === 'file' ? formatBytes(node.size) : '—'}
                </span>
            ),
        },
        {
            key: 'actions',
            title: '',
            width: compact ? 48 : 80,
            align: 'right',
            render: (_: unknown, node) => (
                <span
                    className={classNames(styles['rowActions'])}
                    onClick={(event) => event.stopPropagation()}
                    onDoubleClick={(event) => event.stopPropagation()}
                >
                    {!compact && (
                        <Tooltip title="Властивості">
                            <Button
                                type="text"
                                size="small"
                                icon={<TbInfoCircle />}
                                onClick={() => actions.showProperties(node)}
                                aria-label={`Властивості: ${node.name}`}
                                {...{ [DISK_PROPS_TRIGGER]: '' }}
                            />
                        </Tooltip>
                    )}
                    <Dropdown trigger={['click']} menu={{ items: actions.nodeMenu(node) }} placement="bottomRight">
                        <Button type="text" size="small" icon={<TbDotsVertical />} aria-label={`Дії: ${node.name}`} />
                    </Dropdown>
                </span>
            ),
        },
    ]

    const sharedColumns: typeof columns = [
        {
            key: 'sharedBy',
            title: 'Хто поділився',
            width: 190,
            render: (_: unknown, node) => (
                <span className={classNames(styles['author'])} data-testid="disk-shared-by">
                    <DiskPerson user={node.shared?.sharedBy ?? null} />
                </span>
            ),
        },
        {
            key: 'sharedAt',
            title: 'Надано',
            width: 150,
            render: (_: unknown, node) => (
                <span className={classNames(styles['muted'])}>{formatDateTimeUk(node.shared?.sharedAt)}</span>
            ),
        },
        {
            key: 'role',
            title: 'Моя роль',
            width: 120,
            render: (_: unknown, node) => (
                <span className={classNames(styles['muted'])}>{node.role ? DISK_ROLE_LABEL[node.role] : '—'}</span>
            ),
        },
    ]

    const allColumns = sharedList
        ? [columns[0], ...sharedColumns, columns[columns.length - 1]].filter(
              (column): column is NonNullable<typeof column> => column !== undefined
          )
        : columns

    const visibleColumns = compact
        ? allColumns.filter((column) => column.key === 'name' || column.key === 'actions')
        : narrow
          ? allColumns.filter((column) => column.key !== 'author' && column.key !== 'sharedBy')
          : allColumns

    const rowProps = (node: DiskNode): HTMLAttributes<HTMLElement> => {
        const isFolder = node.type === 'folder'
        const canDrag = node.permissions.update === true

        return {
            className: classNames(isFolder && store.dropTarget === node.uuid && styles['rowDrop']),
            onClick: (event: MouseEvent) =>
                store.click(node.uuid, { ctrl: event.ctrlKey || event.metaKey, shift: event.shiftKey }),
            onDoubleClick: () => actions.open(node),
            draggable: canDrag,
            ...(canDrag
                ? {
                      onDragStart: (event: DragEvent) => {
                          store.startDrag(node)
                          event.dataTransfer.effectAllowed = 'move'
                          event.dataTransfer.setData('text/plain', node.name)
                      },
                      onDragEnd: () => store.endDrag(),
                  }
                : {}),
            ...(isFolder && node.permissions.write
                ? {
                      onDragOver: (event: DragEvent) => {
                          if (!store.canDropOn(node.uuid)) return
                          event.preventDefault()
                          event.dataTransfer.dropEffect = 'move'
                          store.setDropTarget(node.uuid)
                      },
                      onDragLeave: () => store.setDropTarget(null),
                      onDrop: (event: DragEvent) => {
                          event.preventDefault()
                          void actions.drop(node.uuid)
                      },
                  }
                : {}),
        }
    }

    return (
        <Table<DiskNode>
            className={classNames(styles['table'])}
            size="middle"
            rowKey="uuid"
            columns={visibleColumns}
            dataSource={store.visibleItems}
            loading={store.isLoading || store.isSearching}
            pagination={false}
            // Порожній стан малює екран (`Empty` з діями); тут — лише поки вантажиться
            locale={{ emptyText: ' ' }}
            showHeader={!compact}
            tableLayout="fixed"
            showSorterTooltip={false}
            rowSelection={{
                selectedRowKeys: store.selected,
                onChange: (keys) => store.setSelected(keys.map(String)),
                columnWidth: compact ? 36 : 44,
            }}
            onChange={(_pagination, _filters, sorter) => {
                if (!sortable) return

                const current = Array.isArray(sorter) ? sorter[0] : sorter
                const field = current?.columnKey

                if (field !== 'name' && field !== 'updatedAt' && field !== 'size') return

                store.setSort(
                    current?.order
                        ? { field, order: current.order === 'ascend' ? 'asc' : 'desc' }
                        : { field: 'name', order: 'asc' }
                )
            }}
            onRow={rowProps}
            components={{ body: { row: ContextMenuRow } }}
            data-testid="disk-table"
        />
    )
})

/**
 * Права кнопка на рядку відкриває те саме меню, що «⋯», замість меню браузера;
 * поза рядками лишається звичайне меню браузера. Рядок під курсором стає
 * вибраним, якщо ще не вибраний, — дії меню стосуються саме його.
 */
const ContextMenuRow = observer((props: HTMLAttributes<HTMLTableRowElement> & { 'data-row-key'?: string }) => {
    const actions = useDiskActions()
    const node = store.visibleItems.find((item) => item.uuid === props['data-row-key'])

    if (!node) return <tr {...props} />

    return (
        <Dropdown
            trigger={['contextMenu']}
            menu={{ items: actions.nodeMenu(node) }}
            onOpenChange={(open) => {
                if (open && !store.isSelected(node.uuid)) store.setSelected([node.uuid])
            }}
        >
            <tr {...props} />
        </Dropdown>
    )
})
